/**
 * REL-24 — the NATIVE subscription surface (StoreKit).
 *
 * This component is the whole native fork. `SubscriptionSection` hands it the
 * tier data and renders nothing else on native, which is how guideline 3.1.1
 * is satisfied: no Stripe, no web checkout, no billing portal, no price string
 * of our own, and no `/api/pricing` / `/api/checkout` / `/api/billing/portal`
 * call can be reached from the shell.
 *
 * ⚠️ THE FORK IS UNCONDITIONAL; ONLY THE PURCHASE CONTROLS RIDE THE FLAG.
 * `storekit_v1` gates the buy rows. It deliberately does NOT gate the removal
 * of the Stripe surface: server flags resolve from a boot-time /api/config
 * read, so a flag that fails closed on a network hiccup would put an external
 * payment reference in front of App Review. Flag off ⇒ this panel still
 * renders, minus the purchase controls.
 *
 * WHAT APP REVIEW LOOKS FOR, and where each item lives here:
 *   · product name + what it includes  → StoreKit `displayName` / `description`
 *   · the LENGTH of the subscription   → `periodLabel()` off StoreKit's period
 *   · the price, localized             → StoreKit `displayPrice`, never ours
 *   · auto-renewal disclosure          → RENEWAL_DISCLOSURE below
 *   · a working Restore Purchases      → `onRestore`, always rendered
 *   · Privacy Policy + Terms of Use    → the two links under the controls
 *
 * 🔒 SOURCE OF TRUTH (bug-bash #19, 2026-08-29): the plan name and "does this
 * account have a membership" both come from `data.tier` — the field the SERVER
 * enforces on. `subscriptionStatus` describes the BILLING RAIL only and is
 * limited here to the renewal date and the Apple management line. See TIER_LABEL.
 *
 * MINOR GATE (founder ruling 2026-08-19): an athlete whose derived age is
 * UNDER 18 never sees purchase controls; they get the guardian line instead.
 * Restore stays, because restoring is not buying and a family-shared
 * membership is exactly the case it recovers. Unknown age reads as adult here,
 * matching the existing gate posture (REL-29 makes age deterministic).
 *
 * 🏷️ EVERY STRING IN THIS FILE IS PROVISIONAL and listed in
 * `.claude/briefs/REL24-BUILD-REPORT.md` for founder ratification. House
 * register: no exclamations, no "safe", no dashes.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthFetch } from '../../../hooks/useAuthFetch';
import type { UserTierData } from '../../../hooks/useUserTier';
import {
  canMakePayments,
  loadProducts,
  purchaseProduct,
  restorePurchases,
  type StoreKitProduct,
} from '../../../lib/billing/storeKit';
import styles from './NativeSubscriptionPanel.module.css';

/** Apple's standard EULA — the Terms of Use of record (REL-19, founder). */
const APPLE_EULA_URL = 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/';
const PRIVACY_URL = 'https://tayro.run/privacy';

/** PROVISIONAL. The auto-renewal disclosure App Review expects beside the
 *  purchase controls. Written to be true of what StoreKit actually does. */
const RENEWAL_DISCLOSURE =
  'Your membership renews automatically until you cancel. Payment is charged to your Apple Account when you confirm the purchase. You can cancel at any time in your Apple Account settings, at least 24 hours before the current period ends.';

/** PROVISIONAL. The calm guardian line an under-18 athlete sees in place of
 *  the purchase controls. */
const GUARDIAN_LINE =
  'Memberships for athletes under 18 are set up by a parent or guardian. Ask them to open Tayro on their own device.';

/** PROVISIONAL. Where an Apple-billed membership is managed. */
const APPLE_MANAGE_LINE = 'Manage or cancel your membership in your Apple Account settings.';

/** PROVISIONAL. Products could not be loaded, or payments are turned off on
 *  this device (Screen Time, parental controls, a managed profile). */
const UNAVAILABLE_LINE = 'Memberships are not available on this device right now.';

interface Props {
  data: UserTierData;
  /** `storekit_v1`. Off ⇒ no purchase controls (the rest of the panel stays). */
  purchaseEnabled: boolean;
}

/**
 * 🐞 BUG-BASH #19 (Build 26) — THE PLAN NAME READS `tier`, NEVER
 * `subscriptionStatus`.
 *
 * The founder's account showed "Free" on this panel while the server was
 * correctly serving it as `athlete` (many messages, every coach). The panel was
 * not reading the tier at all: it derived the whole plan name from
 * `subscriptionStatus`, which is a STRIPE-ERA column on `users`.
 *
 * `subscriptionStatus` and `tier` are two different fields with two different
 * write paths, and only `tier` is what the server ENFORCES on — `getUserTier()`
 * resolves it and every gate (coach allowlist, msgs/day, races, season
 * planning) reads its `tier_config`. Every grant that moves `tier` WITHOUT
 * landing `subscription_status = 'active'` therefore rendered as "Free" on a
 * fully entitled account:
 *   · a promo / beta grant (writes `tier` + `beta_expires_at` only)
 *   · a family-covered athlete (`tier` = 'family', the PAYER holds the status)
 *   · a team-covered athlete (tier inherited from `team_id`)
 *   · a manual or admin tier grant
 *   · any window where the StoreKit stamp landed the tier but the status read
 *     is stale (deletion → re-claim leaves exactly this kind of skew)
 * …and, in the opposite direction, a lapsed account left holding
 * `status = 'past_due'` with `tier` already dropped to 'free' was shown a paid
 * plan AND denied the buy rows.
 *
 * So entitlement below is `tier !== 'free'` — the same question the server
 * answers — and the label comes from the tier. Labels mirror the web
 * `SubscriptionSection` TIER_DISPLAY so one account reads the same on both
 * surfaces. `subscriptionStatus` keeps only the jobs that are genuinely about
 * the BILLING rail (the renewal date and the Apple management line).
 */
const TIER_LABEL: Record<UserTierData['tier'], string> = {
  free: 'Free',
  athlete: 'Athlete',
  family: 'Family',
  team_25: 'Team 25',
  team_50: 'Team 50',
  team_100: 'Team 100',
};

/** "per month" / "per year" / "per 3 months" from StoreKit's own period. */
function periodLabel(product: StoreKitProduct): string | null {
  const value = product.periodValue;
  const unit = product.periodUnit;
  if (!value || !unit || unit === 'unknown') return null;
  if (value === 1) return `per ${unit}`;
  return `per ${value} ${unit}s`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function NativeSubscriptionPanel({ data, purchaseEnabled }: Props) {
  const fetchAuthed = useAuthFetch();

  // The server's own answer to "does this account have a membership": the tier
  // it resolved. See TIER_LABEL above for why this is NOT `subscriptionStatus`.
  const entitled = data.tier !== 'free';
  // Unknown tier (the server added one this build doesn't know) falls back to
  // the pre-fix wording rather than rendering an empty plan row.
  const planLabel = TIER_LABEL[data.tier] ?? (entitled ? 'Athlete' : 'Free');
  const appleBilled = data.subscriptionSource === 'storekit';
  const underAge = data.derivedUnder18 === true;
  // The buy rows need ALL of: the flag, an adult, and no membership already.
  const wantsPurchaseControls = purchaseEnabled && !underAge && !entitled;

  const [products, setProducts] = useState<StoreKitProduct[] | null>(null);
  const [payments, setPayments] = useState<boolean | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (!wantsPurchaseControls) return;
    let alive = true;
    void (async () => {
      const ok = await canMakePayments();
      if (!alive) return;
      setPayments(ok);
      if (!ok) return;
      const list = await loadProducts();
      if (alive) setProducts(list);
    })();
    return () => {
      alive = false;
    };
  }, [wantsPurchaseControls]);

  const onBuy = useCallback(
    async (productId: string) => {
      setBusy(productId);
      setNotice(null);
      setFailure(null);
      const outcome = await purchaseProduct(fetchAuthed, productId);
      setBusy(null);
      if (outcome.status === 'cancelled') return;
      if (outcome.status === 'pending') {
        setNotice('Your request is waiting for approval. Your membership starts once it is approved.');
        return;
      }
      if (outcome.status === 'unavailable') {
        setFailure(UNAVAILABLE_LINE);
        return;
      }
      if (outcome.status === 'failed') {
        setFailure('The purchase did not complete. Nothing was charged.');
        return;
      }
      if (outcome.state.entitled) {
        setNotice('Your membership is active. Reload to see everything it opens.');
      } else {
        setFailure(outcome.state.error || 'We could not confirm your membership. Try Restore purchases.');
      }
    },
    [fetchAuthed],
  );

  const onRestore = useCallback(async () => {
    setBusy('restore');
    setNotice(null);
    setFailure(null);
    const outcome = await restorePurchases(fetchAuthed);
    setBusy(null);
    if (outcome.status === 'restored') {
      setNotice('Your membership is active. Reload to see everything it opens.');
    } else if (outcome.status === 'unavailable') {
      setFailure(UNAVAILABLE_LINE);
    } else {
      setNotice('No membership was found for this Apple Account.');
    }
  }, [fetchAuthed]);

  const renewsLabel = formatDate(data.subscriptionPeriodEnd);

  return (
    <>
      <div className={styles.planRow}>
        <div className={styles.planName}>{planLabel}</div>
        {entitled && renewsLabel && (
          <div className={styles.planMeta}>Renews {renewsLabel}</div>
        )}
      </div>

      {entitled && appleBilled && <p className={styles.manageLine}>{APPLE_MANAGE_LINE}</p>}

      {underAge && !entitled && <p className={styles.guardianLine}>{GUARDIAN_LINE}</p>}

      {wantsPurchaseControls && payments === false && (
        <p className={styles.unavailable}>{UNAVAILABLE_LINE}</p>
      )}

      {wantsPurchaseControls && products !== null && products.length === 0 && payments !== false && (
        <p className={styles.unavailable}>{UNAVAILABLE_LINE}</p>
      )}

      {wantsPurchaseControls && products !== null && products.length > 0 && (
        <div className={styles.buyBlock}>
          {products.map((product) => {
            const period = periodLabel(product);
            return (
              <div key={product.id} className={styles.productCard}>
                <div className={styles.productHead}>
                  <span className={styles.productName}>{product.displayName}</span>
                  {/* LOCALIZED price straight from StoreKit. */}
                  <span className={styles.productPrice}>{product.displayPrice}</span>
                </div>
                {period && <div className={styles.productPeriod}>{period}</div>}
                {product.description && (
                  <div className={styles.productBody}>{product.description}</div>
                )}
                <button
                  type="button"
                  className={styles.buyBtn}
                  disabled={busy !== null}
                  onClick={() => void onBuy(product.id)}
                >
                  {busy === product.id ? 'Opening…' : 'Subscribe'}
                </button>
              </div>
            );
          })}
          <p className={styles.disclosure}>{RENEWAL_DISCLOSURE}</p>
        </div>
      )}

      {/* Restore is always available: App Review requires a working restore
          control, and it is the recovery path for a reinstall, a new device
          and a family-shared membership. It is not a purchase, so it renders
          for an under-18 athlete too. */}
      <div className={styles.footRow}>
        <button
          type="button"
          className={styles.restoreBtn}
          disabled={busy !== null}
          onClick={() => void onRestore()}
        >
          {busy === 'restore' ? 'Checking…' : 'Restore purchases'}
        </button>
      </div>

      {/* Functional links beside the purchase controls, as App Review expects.
          Absolute https + target=_blank so the shell hands them to the system
          browser (NativeLinkGuard only rewrites /v2 paths and ignores _blank). */}
      <p className={styles.legal}>
        <a className={styles.legalLink} href={PRIVACY_URL} target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
        <span className={styles.legalDot} aria-hidden="true">
          ·
        </span>
        <a className={styles.legalLink} href={APPLE_EULA_URL} target="_blank" rel="noreferrer">
          Terms of Use
        </a>
      </p>

      {notice && (
        <p className={styles.notice} role="status">
          {notice}
        </p>
      )}
      {failure && (
        <p className={styles.failure} role="alert">
          {failure}
        </p>
      )}
    </>
  );
}
